import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Inbox, ClipboardList, Layers, Workflow, Users, LogOut, CheckCheck, Tags, BarChart3, KeyRound } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { LOGO, isManager } from "../lib/api";
import { RoleBadge } from "./Badges";
import { MyPasswordRequestBanner } from "./MyPasswordRequestBanner";

const NAV = [
  { to: "/", label: "Overview", icon: LayoutDashboard, id: "dashboard" },
  { to: "/queue", label: "Shared Queue", icon: Inbox, id: "queue" },
  { to: "/my-tasks", label: "My Tasks", icon: ClipboardList, id: "my-tasks" },
  { to: "/done", label: "My History", icon: CheckCheck, id: "done" },
  { to: "/tasks", label: "Tasks & Deadlines", icon: Layers, id: "all-tasks", manager: true },
  { to: "/workload", label: "Workload", icon: BarChart3, id: "workload", manager: true },
  { to: "/processes", label: "Processes", icon: Workflow, id: "processes", manager: true },
  { to: "/users", label: "Team & Approvals", icon: Users, id: "users", admin: true },
  { to: "/roles", label: "Employee Types", icon: Tags, id: "roles", admin: true },
  { to: "/password-requests", label: "Password Requests", icon: KeyRound, id: "password-requests", manager: true },
];

const NavItems = ({ user, compact }) =>
  NAV.filter((n) => (!n.admin || user.is_admin) && (!n.manager || isManager(user))).map((n) => (
    <NavLink
      key={n.to}
      to={n.to}
      end={n.to === "/" || n.to === "/tasks"}
      data-testid={`nav-${n.id}${compact ? "-mobile" : ""}`}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-lg px-3.5 py-2.5 text-sm font-medium transition-colors duration-200 ${
          isActive ? "bg-[#FF6B00] text-white shadow-lg shadow-[#FF6B00]/20" : "text-slate-400 hover:bg-[#1C202E] hover:text-slate-100"
        } ${compact ? "shrink-0 px-3 py-2" : ""}`
      }
    >
      <n.icon className="h-4 w-4" />
      {n.label}
    </NavLink>
  ));

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const doLogout = async () => {
    await logout();
    nav("/login");
  };
  return (
    <div className="flex min-h-screen bg-[#0B0D12] text-slate-100">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-[#282E3D] lg:flex">
        <div className="flex items-center gap-3 px-5 py-5">
          <img src={LOGO} alt="Mathsmania" className="h-10 w-10 rounded-lg bg-white object-contain p-0.5" />
          <div>
            <div className="font-heading text-lg font-bold leading-none">Mathsmania</div>
            <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF6B00]">Ops Manager</div>
          </div>
        </div>
        <nav className="flex flex-1 flex-col gap-1 px-3 py-4">
          <NavItems user={user} />
        </nav>
        <div className="border-t border-[#282E3D] p-4">
          <div className="text-sm font-semibold" data-testid="sidebar-user-name">{user.name}</div>
          <div className="truncate text-xs text-slate-500">{user.email}</div>
          <div className="mt-2 flex flex-wrap gap-1">
            {user.is_admin && <span className="rounded-md border border-orange-500/30 bg-orange-500/10 px-2 py-0.5 font-mono text-[10px] uppercase text-orange-300">Super Admin</span>}
            {!user.is_admin && user.roles.map((r) => <RoleBadge key={r} role={r} />)}
          </div>
          <button data-testid="logout-button" onClick={doLogout} className="mt-4 flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-[#FF6B00]">
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-40 border-b border-[#282E3D] bg-[#0B0D12]/90 backdrop-blur-md lg:hidden">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-2">
              <img src={LOGO} alt="Mathsmania" className="h-8 w-8 rounded bg-white object-contain" />
              <span className="font-heading font-bold">Mathsmania Ops</span>
            </div>
            <button data-testid="logout-button-mobile" onClick={doLogout} className="text-slate-400"><LogOut className="h-4 w-4" /></button>
          </div>
          <nav className="flex gap-1 overflow-x-auto px-3 pb-3"><NavItems user={user} compact /></nav>
        </header>
        <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-8 sm:px-8 lg:px-12 lg:py-12">
          <MyPasswordRequestBanner />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
